import { wrapApiCall } from '@/lib/axios'
import type { AxiosInstance } from 'axios'
import { deserialize } from 'ts-jackson'
import { PageInfo, PaginationOpt, PaginationResponse } from '../model'
import {
  CheckInData,
  CheckInMemberResponse,
  CheckInUndoResult,
  CheckOutData,
  PeakHoursResponse,
} from './model'

export const CheckinService = (client: AxiosInstance) => {
  return {
    async getCheckinList(opt: PaginationOpt): Promise<PaginationResponse<CheckInData>> {
      const { data } = await wrapApiCall(() =>
        client.get<{ pagination: Record<string, unknown>; data: Record<string, unknown>[] }>(
          '/checkin',
          { params: opt }
        )
      )
      return {
        pagination: deserialize(data.pagination, PageInfo),
        data: data.data.map((item) => deserialize(item, CheckInData)),
      }
    },
    async checkin(memberNo: string | number): Promise<CheckInMemberResponse> {
      const { data } = await wrapApiCall<CheckInMemberResponse>(() =>
        client.post('/member/checkin', { memberCode: memberNo, type: 'MANUAL' })
      )
      return data
    },
    async autoCheckin(memberNo: string | number): Promise<{ payload: string }> {
      const { data } = await wrapApiCall<{ payload: string }>(() =>
        client.post('/member/checkin/qrcode', { memberCode: memberNo, type: 'AUTO' })
      )
      return data
    },
    async createQrCheckIn(memberNo: string | number): Promise<{ payload: string }> {
      const { data } = await wrapApiCall<{ payload: string }>(() =>
        client.post(`/member/checkin/qrcode`, { memberCode: memberNo, type: 'MEMBER-QR-CHECKIN' })
      )
      return data
    },
    /**
     * QTY-2049: reverse the use a mis-scanned check-in charged.
     *
     * The row is kept — the visit happened — so this only returns the use and
     * marks the row undone. The server re-checks that this really is the
     * reversible one; the button's own conditions are not trusted.
     */
    async undoCheckin(checkInId: number): Promise<CheckInUndoResult> {
      const { data } = await wrapApiCall<CheckInUndoResult>(() =>
        client.post(`/member/checkin/${checkInId}/undo`)
      )
      return data
    },
    async getPeakHours(from: string, to: string) {
      const query = new URLSearchParams()
      query.append('filter', `checkin_date=gte.${from}`)
      query.append('filter', `checkin_date=lte.${to}`)
      query.append('limit', '500')

      const { data } = await wrapApiCall<PeakHoursResponse>(() =>
        client.get(`/postgrest/peaktime_period_view?${query.toString()}`)
      )

      return data
    },
    async getDailyCheckins(from: string, to: string) {
      const query = new URLSearchParams()
      query.append('filter', `checkin_date=gte.${from}`)
      query.append('filter', `checkin_date=lte.${to}`)
      query.append('limit', '500')

      const { data } = await wrapApiCall<PeakHoursResponse>(() =>
        client.get(`/postgrest/peaktime_period_view?${query.toString()}`)
      )

      return data
    },
    async checkout(checkInId: number): Promise<CheckOutData> {
      const { data } = await wrapApiCall<CheckOutData>(() =>
        client.post('/member/checkout', { checkInId })
      )
      return data
    },
    async getCheckinCard(userId: string | number): Promise<{ cardUid: string | null }> {
      const { data } = await wrapApiCall<{ cardUid: string | null }>(() =>
        client.get(`/member/${encodeURIComponent(String(userId))}/checkin-card`)
      )
      return data ?? { cardUid: null }
    },
    async assignCheckinCard(
      userId: string | number,
      cardUid: string
    ): Promise<{ cardUid: string | null }> {
      const { data } = await wrapApiCall<{ cardUid: string | null }>(() =>
        client.put(`/member/${encodeURIComponent(String(userId))}/checkin-card`, {
          cardUid: cardUid.trim(),
        })
      )
      return data ?? { cardUid: null }
    },
    async removeCheckinCard(userId: string | number): Promise<{ cardUid: string | null }> {
      const { data } = await wrapApiCall<{ cardUid: string | null }>(() =>
        client.delete(`/member/${encodeURIComponent(String(userId))}/checkin-card`)
      )
      return data ?? { cardUid: null }
    },
  }
}
